import { useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Popper from '@material-ui/core/Popper'
import { makeStyles, Theme } from '@material-ui/core/styles'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import clsx from 'clsx'
import { onEnterKeyDown } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React, { useCallback, useMemo } from 'react'
import { SelectionType } from './../index'
import ProductListingIcon, {
    ProductListingIconStatusType,
    ProductListingIconType,
} from './ProductListingIcon'

const useStyles = makeStyles((theme: Theme) => ({
    oneButtonWidth: { width: 120 },
    twoButtonWidth: { width: 180 },
    popperContainer: { backgroundColor: theme.palette.common.white },
    popperHeight: { height: 35 },
    popperSeparator: { borderRight: '1px solid #F2F2F2' },
    popperHovered: {
        color: '#E69C24',
    },
    popperInitial: {
        color: '#A78966',
    },
    elementPointer: {
        cursor: 'pointer',
    },
    bodText: {
        lineHeight: '30px',
        userSelect: 'none',
    },
    deleteIcon: {
        fontSize: pxToRem(30),
        color: '#EB5757',
    },
    disabledPopper: {
        opacity: 0.5,
        cursor: 'default',
    },
}))

export interface ProductListingCardPopperProps {
    listingId?: string
    isSelected: boolean
    selectionType?: SelectionType
    popperAnchorEl?: HTMLDivElement | null
    isProductBOD?: boolean
    isProductDeprecated?: boolean
    isListingManufacturerProductType?: boolean
    onAddProductToProject: () => void
    onAddProductAsBOD: () => void
    onRemoveProduct: () => void
    isFlaggedForReview?: boolean
    onAddFlagForReview: () => void
    onRemoveFlagForReview: () => void
}

const ProductListingCardPopper: React.FC<ProductListingCardPopperProps> = ({
    listingId,
    isSelected,
    selectionType,
    popperAnchorEl,
    isProductBOD,
    isProductDeprecated,
    isListingManufacturerProductType,
    onAddProductToProject,
    onAddProductAsBOD,
    onRemoveProduct,
    isFlaggedForReview,
    onAddFlagForReview,
    onRemoveFlagForReview,
}) => {
    const classes = useStyles()
    const Product = 'Product'
    const BOD = 'BOD'

    const { isReviewer } = useReactiveVar(userInfo)
    const isDisabled = React.useMemo(() => isReviewer, [isReviewer])

    const [iconHover, setIconHover] = React.useState<
        ProductListingIconType | undefined
    >(undefined)
    const isSelectedListingProduct = isSelected && selectionType === Product
    const isSelectedListingBOD = isSelected && selectionType === BOD
    const isFlagForReviewDisable = isReviewer
        ? isSelectedListingProduct || isSelectedListingBOD
        : false

    const handleSetIconHover = useCallback(
        (currentIconHover: ProductListingIconType | undefined) => () =>
            setIconHover(currentIconHover),
        [setIconHover]
    )

    const isApprovedBODProduct = useMemo(
        () => isProductBOD && !isProductDeprecated,
        [isProductBOD, isProductDeprecated]
    )

    if (!listingId) return null

    // const handleAddRemoveToProject = useMemo(
    //     () =>
    //         !isDisabled
    //             ? !isSelectedListingProduct && !isProductDeprecated
    //                 ? onAddProductToProject
    //                 : onRemoveProduct
    //             : undefined,
    //     [
    //         isDisabled,
    //         isSelectedListingProduct,
    //         isProductDeprecated,
    //         onAddProductToProject,
    //         onRemoveProduct,
    //     ]
    // )

    const handleAddRemoveToProject = useMemo(() => {
        if (isDisabled) return undefined
        return !isSelectedListingProduct && !isProductDeprecated
            ? onAddProductToProject
            : onRemoveProduct
    }, [
        isDisabled,
        isSelectedListingProduct,
        isProductDeprecated,
        onAddProductToProject,
        onRemoveProduct,
    ])

    // const AddRemoveAsBasisOfDesign = useMemo(
    //     () =>
    //         !isDisabled
    //             ? !isSelectedListingBOD
    //                 ? onAddProductAsBOD
    //                 : onRemoveProduct
    //             : undefined,
    //     [isDisabled, isSelectedListingBOD, onAddProductAsBOD, onRemoveProduct]
    // )

    const handleBasisOfDesign = useMemo(() => {
        if (isDisabled) return undefined
        return !isSelectedListingBOD ? onAddProductAsBOD : onRemoveProduct
    }, [isDisabled, isSelectedListingBOD, onAddProductAsBOD, onRemoveProduct])

    // const AddRemoveAsFlagForReview = useMemo(
    //     () =>
    //         !isFlagForReviewDisable
    //             ? !isFlaggedForReview
    //                 ? onAddFlagForReview
    //                 : onRemoveFlagForReview
    //             : undefined,
    //     [
    //         isFlagForReviewDisable,
    //         isFlaggedForReview,
    //         onAddFlagForReview,
    //         onRemoveFlagForReview,
    //     ]
    // )

    const handleFlagForReview = useMemo(() => {
        if (isFlagForReviewDisable) return undefined
        return !isFlaggedForReview ? onAddFlagForReview : onRemoveFlagForReview
    }, [
        isFlagForReviewDisable,
        isFlaggedForReview,
        onAddFlagForReview,
        onRemoveFlagForReview,
    ])

    return (
        <Popper
            data-testid={`listingCardPopper${listingId}`}
            open={!!popperAnchorEl}
            anchorEl={popperAnchorEl}
            disablePortal
            keepMounted
            modifiers={{
                offset: {
                    enabled: true,
                    offset: '0, -80', // from the top of the card media
                },
            }}
        >
            <GridContainer
                className={clsx(
                    classes.popperHeight,
                    classes.popperContainer,
                    isListingManufacturerProductType
                        ? classes.oneButtonWidth
                        : isApprovedBODProduct
                        ? classes.twoButtonWidth
                        : classes.oneButtonWidth
                )}
                justifyContent="center"
            >
                {!isListingManufacturerProductType && (
                    <HoverTooltip
                        text={
                            !isSelectedListingProduct
                                ? 'Add to Project'
                                : 'Remove Product from Project'
                        }
                    >
                        <GridContainer
                            item
                            justifyContent="center"
                            alignItems="center"
                            xs={isApprovedBODProduct ? 4 : 6}
                            className={clsx(
                                classes.popperSeparator,
                                !isDisabled && classes.elementPointer
                            )}
                            direction="column"
                            onClick={handleAddRemoveToProject}
                            onKeyDown={onEnterKeyDown(handleAddRemoveToProject)}
                            tabIndex={0}
                            data-testid="dvPopperLeftSide"
                            onMouseOver={handleSetIconHover(
                                ProductListingIconType.ADD
                            )}
                            onMouseLeave={handleSetIconHover(undefined)}
                        >
                            {!isSelectedListingProduct &&
                            !isProductDeprecated ? (
                                <ProductListingIcon
                                    iconType={ProductListingIconType.ADD}
                                    iconStatusType={
                                        isDisabled
                                            ? ProductListingIconStatusType.DISABLED
                                            : iconHover ===
                                              ProductListingIconType.ADD
                                            ? ProductListingIconStatusType.HOVERED
                                            : ProductListingIconStatusType.INITIAL
                                    }
                                />
                            ) : (
                                <DeleteOutlineIcon
                                    className={clsx(
                                        classes.deleteIcon,
                                        classes.popperHeight,
                                        isDisabled && classes.disabledPopper
                                    )}
                                    data-testid={`removeProduct`}
                                />
                            )}
                        </GridContainer>
                    </HoverTooltip>
                )}

                {isApprovedBODProduct && (
                    <HoverTooltip
                        text={
                            !isSelectedListingBOD
                                ? 'Add as Basis of Design'
                                : 'Remove as Basis of Design'
                        }
                    >
                        <GridContainer
                            item
                            justifyContent="center"
                            alignItems="center"
                            xs={!!isListingManufacturerProductType ? 6 : 4}
                            className={clsx(
                                classes.popperSeparator,
                                !isDisabled && classes.elementPointer
                            )}
                            direction="column"
                            onClick={handleBasisOfDesign}
                            onKeyDown={onEnterKeyDown(handleBasisOfDesign)}
                            tabIndex={0}
                            data-testid="dvPopperRightSide"
                            onMouseOver={handleSetIconHover(
                                ProductListingIconType.BOD
                            )}
                            onMouseLeave={handleSetIconHover(undefined)}
                        >
                            {!isSelectedListingBOD ? (
                                <ProductListingIcon
                                    iconType={ProductListingIconType.BOD}
                                    iconStatusType={
                                        isDisabled
                                            ? ProductListingIconStatusType.DISABLED
                                            : iconHover ===
                                              ProductListingIconType.BOD
                                            ? ProductListingIconStatusType.HOVERED
                                            : ProductListingIconStatusType.INITIAL
                                    }
                                />
                            ) : (
                                <DeleteOutlineIcon
                                    className={clsx(
                                        classes.deleteIcon,
                                        classes.popperHeight,
                                        isDisabled && classes.disabledPopper
                                    )}
                                    data-testid={`removeBOD`}
                                />
                            )}
                        </GridContainer>
                    </HoverTooltip>
                )}

                {(!isListingManufacturerProductType ||
                    !!isApprovedBODProduct) &&
                    !isProductDeprecated && (
                        <HoverTooltip
                            text={
                                !isFlaggedForReview
                                    ? 'Flag for review'
                                    : 'Remove flag for review'
                            }
                        >
                            <GridContainer
                                item
                                justifyContent="center"
                                alignItems="center"
                                xs={
                                    !isListingManufacturerProductType &&
                                    !!isApprovedBODProduct
                                        ? 4
                                        : 6
                                }
                                className={clsx(
                                    classes.popperSeparator,
                                    !isFlagForReviewDisable &&
                                        classes.elementPointer
                                )}
                                direction="column"
                                onClick={handleFlagForReview}
                                onKeyDown={onEnterKeyDown(handleFlagForReview)}
                                tabIndex={0}
                                data-testid="dvPopperFlagForReview"
                                onMouseOver={handleSetIconHover(
                                    ProductListingIconType.FLAGFORREVIEW
                                )}
                                onMouseLeave={handleSetIconHover(undefined)}
                            >
                                {!isFlaggedForReview ? (
                                    <ProductListingIcon
                                        iconType={
                                            ProductListingIconType.FLAGFORREVIEW
                                        }
                                        iconStatusType={
                                            isFlagForReviewDisable
                                                ? ProductListingIconStatusType.DISABLED
                                                : iconHover ===
                                                  ProductListingIconType.FLAGFORREVIEW
                                                ? ProductListingIconStatusType.HOVERED
                                                : ProductListingIconStatusType.INITIAL
                                        }
                                    />
                                ) : (
                                    <DeleteOutlineIcon
                                        className={clsx(
                                            classes.deleteIcon,
                                            classes.popperHeight,
                                            isFlagForReviewDisable &&
                                                classes.disabledPopper
                                        )}
                                        data-testid={`removeFlagForReview`}
                                    />
                                )}
                            </GridContainer>
                        </HoverTooltip>
                    )}
            </GridContainer>
        </Popper>
    )
}

export default ProductListingCardPopper
